import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useApiGet } from '../../utils/httpClient';
import { getUserPermissionsView } from '../../endpoints/usersTs';

const formatDateTime = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const scopeLabel = (entry) => {
  if (entry.is_international || entry.access_scope === 'international') {
    return 'International';
  }
  if (entry.access_scope === 'spd') {
    return entry.restricted_to_spd_name
      ? `SPD: ${entry.restricted_to_spd_name}`
      : 'SPD';
  }
  if (entry.access_scope === 'chapter') {
    return entry.restricted_to_chapter_name
      ? `Chapter: ${entry.restricted_to_chapter_name}`
      : 'Chapter';
  }
  return entry.access_scope || 'Unknown';
};

const grantLabel = (entry) => {
  const group = entry.granting_group_name || 'Unknown group';
  const groupType = entry.granting_group_type_name || 'Unknown type';
  return `${groupType} / ${group}`;
};

export default function UserPermissions({ id }) {
  const { data, isFetching } = useApiGet(
    ['userPermissionsView', id],
    getUserPermissionsView,
    { enabled: !!id },
    { id }
  );

  if (isFetching) {
    return <Typography>Loading permissions...</Typography>;
  }

  if (!data) {
    return <Typography>No permissions data available.</Typography>;
  }

  const summary = data.summary || {};
  const applicationEntries = data.effective_application_permissions || [];
  const reportEntries = data.effective_report_permissions || [];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6">Authentication / Status</Typography>
            <Divider sx={{ my: 1.5 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Last login
                </Typography>
                <Typography>{formatDateTime(summary.last_login)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Is active
                </Typography>
                <Typography>{summary.is_active ? 'Yes' : 'No'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  iMIS auth
                </Typography>
                <Typography>{summary.imis_auth ? 'Yes' : 'No'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  iMIS ID
                </Typography>
                <Typography>{summary.imis_id || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6">
              Effective Application Permissions
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            {applicationEntries.length === 0 ? (
              <Typography color="text.secondary">
                No effective application permissions.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Permission</TableCell>
                      <TableCell>Scope</TableCell>
                      <TableCell>Granted By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {applicationEntries.map((entry, index) => (
                      <TableRow
                        key={`${entry.permission_name}-${entry.access_scope}-${index}`}
                      >
                        <TableCell>
                          <Typography variant="body2">
                            {entry.permission_name}
                          </Typography>
                          {entry.permission_description ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {entry.permission_description}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={scopeLabel(entry)} />
                        </TableCell>
                        <TableCell>
                          <Box>{grantLabel(entry)}</Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6">Effective Report Permissions</Typography>
            <Divider sx={{ my: 1.5 }} />
            {reportEntries.length === 0 ? (
              <Typography color="text.secondary">
                No effective report permissions.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Report</TableCell>
                      <TableCell>Permission</TableCell>
                      <TableCell>Scope</TableCell>
                      <TableCell>Granted By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportEntries.map((entry, index) => (
                      <TableRow
                        key={`${entry.permission_name}-${entry.source_id || 'no-source'}-${entry.access_scope}-${index}`}
                      >
                        <TableCell>
                          {entry.source_name || 'Unknown report'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {entry.permission_name}
                          </Typography>
                          {entry.permission_description ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {entry.permission_description}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={scopeLabel(entry)} />
                        </TableCell>
                        <TableCell>
                          <Box>{grantLabel(entry)}</Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
